import type { CategoryConfig, CmpConfig, TranslationConfig } from "./config";

type ButtonsJson = Record<string, string | undefined>;

const CATEGORY_DEFS = [
  { key: "necessary", sortOrder: 0, required: true, titleField: "necessaryTitle", introField: "necessaryIntroduction" },
  { key: "analytics", sortOrder: 1, required: false, titleField: "statisticsTitle", introField: "statisticsIntroduction", preCheckedKey: "statistics" },
  { key: "functional", sortOrder: 2, required: false, titleField: "preferencesTitle", introField: "preferencesIntroduction", preCheckedKey: "preferences" },
  { key: "marketing", sortOrder: 3, required: false, titleField: "marketingTitle", introField: "marketingIntroduction", preCheckedKey: "marketing" },
] as const;

const FALLBACK_LABELS: Record<string, { tr: { title: string; desc: string }; en: { title: string; desc: string } }> = {
  necessary: {
    tr: { title: "Kesinlikle gerekli", desc: "Web sitesinin çalışması için zorunludur." },
    en: { title: "Necessary", desc: "Required for the website to function." },
  },
  analytics: {
    tr: { title: "İstatistik", desc: "Ziyaretçi istatistiklerini toplar." },
    en: { title: "Statistics", desc: "Collects visitor statistics." },
  },
  functional: {
    tr: { title: "Tercihler", desc: "Tercihlerinizi hatırlar." },
    en: { title: "Preferences", desc: "Remembers your preferences." },
  },
  marketing: {
    tr: { title: "Pazarlama", desc: "Kişiselleştirilmiş reklamlar için kullanılır." },
    en: { title: "Marketing", desc: "Used for personalized advertising." },
  },
};

function labelsFromTranslation(tr: TranslationConfig | undefined, def: (typeof CATEGORY_DEFS)[number]): { nameJson: Record<string, string>; descJson: Record<string, string> } {
  const buttons = (tr?.buttonsJson || {}) as ButtonsJson;
  const lang = tr?.language || "tr";
  const fb = FALLBACK_LABELS[def.key][lang === "en" ? "en" : "tr"];
  return {
    nameJson: { [lang]: buttons[def.titleField] || fb.title },
    descJson: { [lang]: buttons[def.introField] || fb.desc },
  };
}

function mergeLabels(
  translations: TranslationConfig[],
  def: (typeof CATEGORY_DEFS)[number]
): { nameJson: Record<string, string>; descJson: Record<string, string> } {
  const nameJson: Record<string, string> = {};
  const descJson: Record<string, string> = {};
  for (const tr of translations) {
    const part = labelsFromTranslation(tr, def);
    Object.assign(nameJson, part.nameJson);
    Object.assign(descJson, part.descJson);
  }
  if (Object.keys(nameJson).length === 0) {
    nameJson.tr = FALLBACK_LABELS[def.key].tr.title;
    nameJson.en = FALLBACK_LABELS[def.key].en.title;
    descJson.tr = FALLBACK_LABELS[def.key].tr.desc;
    descJson.en = FALLBACK_LABELS[def.key].en.desc;
  }
  return { nameJson, descJson };
}

/**
 * Admin stores category labels in translation buttonsJson; DB categories may be empty.
 * Fall back to buttonsJson (and defaults) so the preferences modal matches admin preview.
 */
export function resolveCategories(config: CmpConfig, lang?: string): CategoryConfig[] {
  if (config.categories?.length > 0) {
    return config.categories;
  }

  const theme = (config.themeJson || {}) as {
    compliance?: { preChecked?: { preferences?: boolean; statistics?: boolean; marketing?: boolean } };
  };
  const preChecked = theme.compliance?.preChecked || {};
  const translations = config.translations?.length ? config.translations : [];

  return CATEGORY_DEFS.map((def) => {
    const { nameJson, descJson } = mergeLabels(translations, def);
    const defaultEnabled = def.required
      ? true
      : def.preCheckedKey
        ? !!preChecked[def.preCheckedKey as keyof typeof preChecked]
        : false;

    return {
      id: def.key,
      key: def.key,
      required: def.required,
      defaultEnabled,
      sortOrder: def.sortOrder,
      nameJson,
      descJson,
    };
  });
}
